import { classNames, ICONS, toggleItem } from '@fl/cmsch-fe-library';
import { isEmpty } from 'lodash/fp';
import React, { createRef, FC, memo, ReactNode, RefObject, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import { MenuItem } from 'common/layout/types/menu-item';
import { LanguageOption, LanguageSelect } from './language-select';
import { buildSubMenuItems, isActive, MainMenuItem } from './main-menu-item';
import { SideMenu } from './side-menu';
import { SmallMenu } from './small-menu';
import styles from './styles.sass';
import logoDevelopment from './img/logo-development.png';
import logoProduction from './img/logo-production.png';
import logoStaging from './img/logo-staging.png';
type Environment = 'development' | 'staging' | 'production';
/** Supported menu presentations for the shared shell navigation. */
export type MenuType = 'full' | 'side' | 'small';

/** Props for the responsive main menu that powers the app shell navigation. */
interface Props {
  /** Navigation tree shown in the current shell variant. */
  menuItems: Array<MenuItem>;
  /** Current router pathname used to mark active items. */
  locationPath: string;
  /** Target path for the main logo link. */
  homePath: string;
  /** Whether the user state is still loading. */
  isUserLoading: boolean;
  /** Responsive menu variant selected by the shell. */
  menuType: MenuType;
  /** Available languages rendered in the shell picker. */
  languageOptions: Array<LanguageOption>;
  /** Currently selected language option. */
  selectedLanguage: LanguageOption;
  /** Runtime environment used to pick the correct logo asset. */
  environment: Environment;
  /** Whether the side menu is currently collapsed. */
  isSideMenuCollapsed: boolean;
  /** Optional click handler invoked after a menu navigation. */
  onMenuItemClick?(): void;
  /** Persists a language change back into the user state. */
  onLanguageChange(language: string): void;
}
const environmentLogoMap: Record<Environment, string> = {
  production: logoProduction,
  development: logoDevelopment,
  staging: logoStaging
};
const renderMenuItems = (menuItems: Array<MenuItem>, locationPath: string, overflowedMenuItemsKeys: Array<string>, actionIconsRef: RefObject<HTMLDivElement>, toggleOverflowedMenuItem: (key: string) => void): ReactNode => menuItems.map((menuItem: MenuItem) => <MainMenuItem key={menuItem.key} locationPath={locationPath} menuItem={menuItem} visible={!overflowedMenuItemsKeys.includes(menuItem.key)} actionIconsRef={actionIconsRef} toggleOverflowedMenuItem={toggleOverflowedMenuItem} />);
const MainMenuBase: FC<Props> = ({
  menuItems,
  locationPath,
  homePath,
  isUserLoading,
  menuType,
  languageOptions,
  selectedLanguage,
  environment,
  isSideMenuCollapsed,
  onMenuItemClick,
  onLanguageChange
}) => {
  const {
    t
  } = useOurTranslation('user');
  const [overflowedMenuItemsKeys, setOverflowedMenuItems] = useState<Array<string>>([]);
  const toggleOverflowedMenuItem = useCallback((key: string) => {
    setOverflowedMenuItems(toggleItem(key)(overflowedMenuItemsKeys));
  }, [overflowedMenuItemsKeys]);
  useEffect(() => {
    setOverflowedMenuItems([]);
  }, [menuItems]);
  const appLogo = <img className={styles.logo} src={environmentLogoMap[environment]} alt="eskot" />;
  const languageSelect = <LanguageSelect isSideMenu={false} languageOptions={languageOptions} selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} />;
  const actionIconsRef = createRef<HTMLDivElement>();
  const overflowedMenuItems = menuItems.filter(x => overflowedMenuItemsKeys.includes(x.key));
  const overflowedMenuClass = classNames(styles.menuItem, styles.moreButton, 'd-inline-block', isActive(locationPath, homePath, overflowedMenuItems) && styles.active);
  const overflowedMenu = <Ant.Dropdown overlay={<Ant.Menu items={buildSubMenuItems(overflowedMenuItems, locationPath)} />} placement="bottomRight" arrow>
            <div className={overflowedMenuClass}>
                {ICONS.moreOutlined}
            </div>
        </Ant.Dropdown>;
  const actionIcons = <div className={classNames('position-absolute', styles.actionIcons)} ref={actionIconsRef}>
            {languageSelect}
            {!isEmpty(overflowedMenuItemsKeys) && menuType === 'full' && overflowedMenu}
        </div>;
  switch (menuType) {
    case 'side':
      return <SideMenu isUserLoading={isUserLoading} menuItems={menuItems} locationPath={locationPath} onMenuItemClick={onMenuItemClick} isSideMenuCollapsed={isSideMenuCollapsed} />;
    case 'small':
      return <SmallMenu appLogo={appLogo} actionIcons={actionIcons} />;
    case 'full':
      return <div className={styles.menuContainer}>
                    {isUserLoading ? <div className={styles.loadingHeader}>{t('loadingUser')}</div> : <div className={classNames('text-nowrap position-relative overflow-hidden', styles.menuHeight)}>
                                <div className={classNames(styles.menuItem, styles.logoItem)}>
                                    <Link to={homePath} onClick={onMenuItemClick}>
                                        {appLogo}
                                    </Link>
                                </div>
                                {renderMenuItems(menuItems, locationPath, overflowedMenuItemsKeys, actionIconsRef, toggleOverflowedMenuItem)}
                                {actionIcons}
                            </div>}
                </div>;
  }
};

/**
 * Renders the responsive top-level menu that powers the app shell navigation.
 *
 * @param props Menu, language, and environment state for the shell navigation.
 * @returns The responsive main menu for the current shell breakpoint.
 *
 * @example
 * <MainMenu
 *     menuItems={menuItems}
 *     locationPath="/home"
 *     homePath="/home"
 *     isUserLoading={false}
 *     menuType="full"
 *     languageOptions={languageOptions}
 *     selectedLanguage={selectedLanguage}
 *     environment="production"
 *     isSideMenuCollapsed={false}
 *     onLanguageChange={handleLanguageChange}
 * />
 */
export const MainMenu = memo(MainMenuBase);